import { hslaToHsva } from '@uiw/react-color';
import memoizee from 'memoizee';
import { type ThemeType } from '~/_shared/themes/themeType';
import { clamp } from '~/_shared/utils/number/number.helpers';
import {
  isNullsy, notNullsy,
} from '~/_shared/utils/typeGuards';
import {
  createColor, guaranteeHash, hexAndOpacityToRGBA, rgbColorToString,
} from '../../components/colorPicker/colorPicker.helpers';
import {
  type ColorResult, type RgbaColor,
} from '../../components/colorPicker/colorPicker.types';
import { memoizeWeak } from '../memoize/memoize';
import {
  type HexColor, type PerThemeColor,
} from './colors.types';

const MIN_READABLE_CONTRAST = 4.5;
export const BARELY_READABLE_CONTRAST = 1.2;
const LIGHT_DARK_THRESHOLD = 130;

export const getNextColorFromPresets = (usedColors: ReadonlyArray<string>, presetColors: readonly [string, ...string[]]): string => {
  const normalizedPresetColors = presetColors.map(color => guaranteeHash(color).toUpperCase());
  const normalizedUsedColors = usedColors.map(color => guaranteeHash(color).toUpperCase());

  let lastUsedPresetColor = normalizedPresetColors[normalizedPresetColors.length - 1];

  for (const usedRouteColor of normalizedUsedColors) {
    if (normalizedPresetColors.includes(usedRouteColor)) {
      lastUsedPresetColor = usedRouteColor;
    }
  }

  let indexOfNewColor = normalizedPresetColors.indexOf(lastUsedPresetColor ?? '') + 1;
  if (indexOfNewColor > normalizedPresetColors.length - 1) {
    indexOfNewColor = 0;
  }

  // TODO: Change to @ts-expect-error when we transition to the stricter TS config.
  // eslint-disable-next-line
  // @ts-ignore: The indexing is made safe by the condition above.
  return normalizedPresetColors[indexOfNewColor];
};

export const changeColorAlpha = memoizeWeak((color: RgbaColor, newAlpha: number): ColorResult => {
  const newColor = { ...color };
  newColor.a = newAlpha;

  return createColor(newColor);
});

export const convertColorToWebGLColor = memoizee((color: string, opacity?: number): WebglColor => {
  const parsedColor = createColor(color).rgb;
  const base = [parsedColor.r, parsedColor.g, parsedColor.b] as const;

  return [...base, opacity ?? 1];
});

export const webGLColorToString = (color: WebglColor): string =>
  `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`;

export const convertRGBColorToWebGLColor = memoizeWeak((color: RgbaColor): WebglColor =>
  Object.values({ ...color, a: color.a }) as unknown as WebglColor);

const getColorBrightness = (color: RgbaColor) =>
  Math.sqrt(
    color.r * color.r * .241 +
    color.g * color.g * .691 +
    color.b * color.b * .068,
  );

export const getSuitableBorderColor = (baseColor: ColorResult, backgroundColor?: ColorResult): ColorResult => {
  const backgroundBrightness = getColorBrightness((backgroundColor || baseColor).rgba);
  const baseBrightness = getColorBrightness(baseColor.rgba);

  let { l: baseLightness, s: baseSaturation } = baseColor.hsl;
  const baseHue = baseColor.hsl.h;

  // Choose a delta based on the hue
  let delta = 15; // default
  if (baseHue >= 30 && baseHue <= 70) {
    // For yellows
    delta = 25;
  }
  else if (baseHue > 70 && baseHue < 180) {
    // For greens
    delta = 20;
  }
  else if (baseHue >= 180 && baseHue <= 240) {
    // For blues
    delta = 20;
  }
  else if (baseHue >= 300 && baseHue <= 330) {
    // For pinks/magentas
    delta = 20;
  }
  else if (baseHue < 30 || baseHue > 330) {
    // For reds
    delta = 25;
  }

  if (Math.abs(backgroundBrightness - baseBrightness) < 35) {
    if (backgroundBrightness > LIGHT_DARK_THRESHOLD) {
      baseLightness -= delta;
    }
    else {
      if (baseLightness < 5) {
        baseSaturation = 2;
        baseLightness = 60;
      }
      else {
        baseLightness += delta;
      }
    }
  }

  return createColor(hslaToHsva({
    ...baseColor.hsla,
    l: baseLightness,
    s: baseSaturation,
  }));
};

export const getSuitableBorderColorString = (baseColor: string, backgroundColor: string): string => {
  return rgbColorToString(getSuitableBorderColor(createColor(baseColor), createColor(backgroundColor)).rgba);
};

export const makeColorLighter = (baseColor: string, lightnessDifference = 30): ColorResult => {
  const colorResultColor = createColor(baseColor);
  const newHslColor = {
    ...colorResultColor.hsla,
    l: Math.min(colorResultColor.hsl.l + lightnessDifference, 100),
  };

  return createColor(hslaToHsva(newHslColor));
};

export const makeColorDarker = (baseColor: string, lightnessDifference = 20): ColorResult => {
  const colorResultColor = createColor(baseColor);

  const newHslColor = {
    ...colorResultColor.hsla,
    l: Math.max(colorResultColor.hsl.l - lightnessDifference, 0),
  };

  return createColor(hslaToHsva(newHslColor));
};

/**
 * Checks if the contrast between a base color and an optional background color is sufficient, using the WCAG contrast
 * ratio.
 *
 * @param baseColor - The base (foreground) color as a hex code.
 * @param backgroundColor - The background color as a hex code.
 * @param minContrastRatio - The minimum acceptable contrast ratio (default is 4.5).
 * @returns True if the contrast ratio between baseColor and backgroundColor is at least minContrastRatio,
 *          otherwise false.
 */
export const isContrastSufficient = (
  baseColor: string,
  backgroundColor?: string,
  minContrastRatio: number = MIN_READABLE_CONTRAST
): boolean =>
  backgroundColor
    ? getColorReadability(baseColor, backgroundColor) >= minContrastRatio
    : false;

/**
 * Gets a contrast color based on the base color and optional background color.
 *
 * @param baseColor - The base color to get the contrast for.
 * @param backgroundColor - The background color to compare against.
 * @param options - Optional settings for the contrast calculation.
 * @param options.tolerance - The tolerance level for brightness difference.
 * Brightness difference below this level will trigger a different contrast color.
 * Default is 85. Max value is 255 and min value is 0.
 * @returns The contrast color.
 */
export const getContrastColor = (
  baseColor: ColorResult,
  backgroundColor?: ColorResult,
  options?: { tolerance?: number }
) => getContrastColorMemoized(baseColor, backgroundColor, options?.tolerance);

const getContrastColorMemoized = memoizeWeak((
  baseColor: ColorResult,
  backgroundColor: ColorResult | undefined,
  tolerance?: number,
): ColorResult => {
  const backgroundBrightness = backgroundColor && getColorBrightness(backgroundColor.rgba);
  const baseBrightness = getColorBrightness(baseColor.rgba);

  let baseLightness = baseColor.hsl.l;

  if (!isContrastSufficient(baseColor.hex, backgroundColor?.hex, tolerance)) {
    if ((backgroundBrightness ?? baseBrightness) > 130) {
      baseLightness = 15;
    }
    else {
      baseLightness = 85;
    }
  }

  return createColor(hslaToHsva({
    ...baseColor.hsla,
    l: baseLightness,
  }));
});

export const getDarkOrLightDependingOnBaseColor = (baseColor: ColorResult, darkColor: ColorResult, lightColor: ColorResult): ColorResult => {
  const baseBrightness = getColorBrightness(baseColor.rgba);

  return baseBrightness > LIGHT_DARK_THRESHOLD ? darkColor : lightColor;
};

export const getFontAndBorderColorOnBackground = (
  fontColor: ColorResult, baseColor: ColorResult, backgroundColor: ColorResult,
): [borderColor: ColorResult, fontColor: ColorResult] => {
  const contrastTextColor = getContrastColor(fontColor, baseColor);
  const borderColor = getSuitableBorderColor(baseColor, backgroundColor);

  return [borderColor, contrastTextColor];
};

export const mixColorMemoized = memoizee((
  c1: string, c2: string, amount?: number,
) => mixColors(c1, c2, notNullsy(amount) ? amount * 100 : undefined));

export const getPerThemeColor = (perThemeColor: PerThemeColor, currentTheme: ThemeType): string | null => (
  perThemeColor[currentTheme] ?? null
);

const mixColors = (color1: string, color2: string, amount: number = 50) => {
  const rgb1 = createColor(color1).rgba;
  const rgb2 = createColor(color2).rgba;

  const p = amount / 100;
  const rgba = {
    r: (rgb2.r - rgb1.r) * p + rgb1.r,
    g: (rgb2.g - rgb1.g) * p + rgb1.g,
    b: (rgb2.b - rgb1.b) * p + rgb1.b,
    a: (rgb2.a - rgb1.a) * p + rgb1.a,
  };

  return createColor(rgba);
};

const getColorReadability = (color1: string, color2: string) => {
  const c1 = createColor(color1);
  const c2 = createColor(color2);
  return (Math.max(getColorLuminance(c1), getColorLuminance(c2)) + 0.05) / (Math.min(getColorLuminance(c1), getColorLuminance(c2)) + 0.05);
};

const getColorLuminance = (color1: ColorResult) => {
  const rgb = color1.rgb;
  let r, g, b;
  const RsRGB = rgb.r / 255;
  const GsRGB = rgb.g / 255;
  const BsRGB = rgb.b / 255;
  if (RsRGB <= 0.03928) {
    r = RsRGB / 12.92;
  }
  else {
    r = Math.pow((RsRGB + 0.055) / 1.055, 2.4);
  }
  if (GsRGB <= 0.03928) {
    g = GsRGB / 12.92;
  }
  else {
    g = Math.pow((GsRGB + 0.055) / 1.055, 2.4);
  }
  if (BsRGB <= 0.03928) {
    b = BsRGB / 12.92;
  }
  else {
    b = Math.pow((BsRGB + 0.055) / 1.055, 2.4);
  }
  return .2126 * r + .7152 * g + .0722 * b;
};

/**
 * Appends an alpha value to a hex color
 *
 * @param hex - A 6-digit hex color (with or without a leading '#').
 * @param opacity - A number between 0 and 1 representing opacity.
 * @returns An 8-digit hex color string (with a leading '#').
 */
export function hexWithOpacity(hex: string, opacity: number): string {
  const clampedOpacity = clamp(opacity, { min: 0, max: 1 });

  const color = hexAndOpacityToRGBA(hex, clampedOpacity);
  return createColor(color).hexa.toUpperCase();
}

export const calculateGradientColor = ({ position, totalCount, colors }: {
  position: number;
  totalCount: number;
  colors: NonEmptyArray<HexColor>;
}): HexColor => {
  if (totalCount <= 1 || colors.length <= 1) {
    return colors[0];
  }

  const value = position * (100 / (totalCount - 1));

  // Multiple colors: find the appropriate segment
  const segmentSize = 100 / (colors.length - 1);
  const segmentIndex = Math.floor(value / segmentSize);

  // Clamp segment index to avoid out-of-bounds
  const clampedIndex = Math.min(segmentIndex, colors.length - 2);

  const step = (value - clampedIndex * segmentSize) / segmentSize;

  const low = colors[clampedIndex];
  const high = colors[clampedIndex + 1];

  if (isNullsy(low) || isNullsy(high)) { // Invariant check, should be unreachable due to clamping
    throw new Error(`Invalid gradient calculation: Unable to interpolate colors. Low color '${low}' or high color '${high}' is missing. Colors array: [${colors.join(', ')}], segment index: ${clampedIndex}, position: ${position}/${totalCount}`);
  }

  const lowColor = createColor(low);
  const highColor = createColor(high);

  const r = generateRGBColorSegment(lowColor.rgba, highColor.rgba, 'r', step);
  const g = generateRGBColorSegment(lowColor.rgba, highColor.rgba, 'g', step);
  const b = generateRGBColorSegment(lowColor.rgba, highColor.rgba, 'b', step);

  return `#${r}${g}${b}`;
};

const generateRGBColorSegment = (color1: RgbaColor, color2: RgbaColor, key: 'r' | 'g' | 'b', step: number): string => {
  const diff = color2[key] - color1[key];
  const colorPart = (Math.round(diff * step) + color1[key]).toString(16);

  return colorPart.length === 1 ? '0' + colorPart : colorPart;
};
